<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :search="search"
    :no-results-text="$t('audit.users.noResults')"
    :items-per-page="-1"
    dense
    multi-sort
    style="white-space: nowrap;"
  >
    <template #item.lastOnlineAt="{ item }">
      {{
        new Date(item.lastOnlineAt).toLocaleDateString(undefined, {
          timeZone: "CET",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      }}
    </template>

    <template #item.actions="{ item }">
      <v-btn :to="{ name: 'auditAuditPeriod', params: { id: item.id } }" color="secondary" small icon>
        <v-icon>mdi-eye</v-icon>
      </v-btn>
    </template>

    <template #foot="">
      <tfoot class="totals">
        <tr>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td>{{ totals.total }}</td>
          <td>{{ totals.minimal }}</td>
          <td>{{ totals.stepModel }}</td>
          <td>{{ totals.new }}</td>
          <td>{{ totals.editMinimal }}</td>
          <td>{{ totals.editStep }}</td>
          <td>{{ totals.secondEditStep }}</td>
          <td>{{ totals.finalEditStep }}</td>
          <td>{{ totals.auditMinimal }}</td>
          <td>{{ totals.auditStep }}</td>
          <td>{{ totals.secondAudit }}</td>
          <td>{{ totals.interview }}</td>
          <td>{{ totals.interviewAudit }}</td>
          <td>{{ totals.finalAuditMinimal }}</td>
          <td>{{ totals.finalAuditStep }}</td>
          <td>{{ totals.closeable }}</td>
          <td>{{ totals.done }}</td>
        </tr>
      </tfoot>
    </template>
  </v-data-table>
</template>

<script>
import { length, filter, includes, propSatisfies, __, curry, map, sum, pluck } from "ramda";

const countFunds = curry((prop, states, funds) => length(filter(propSatisfies(includes(__, states), prop), funds)));

const countEditStep = (funds) =>
  length(
    filter((fund) => {
      return fund.stepModel && fund.stepModelRequirementsState === "EDIT";
    }, funds)
  );

export default {
  props: {
    auditPeriods: {
      type: Array,
      required: true,
    },

    search: {
      type: String,
      default: "",
    },
  },

  computed: {
    headers() {
      return [
        {
          text: "",
          value: "actions",
          filterable: false,
          sortable: false,
          align: "right",
        },
        {
          text: "#",
          value: "number",
        },
        {
          text: this.$t("auditor.auditPeriods.auditor"),
          value: "auditor",
        },
        {
          text: this.$t("admin.users.company"),
          value: "name",
        },
        {
          text: this.$t("auditor.auditPeriods.name"),
          value: "userName",
        },
        {
          text: this.$t("auditor.auditPeriods.lastOnlineAt"),
          value: "lastOnlineAt",
        },
        {
          text: this.$t("auditor.auditPeriods.totalFunds"),
          value: "total",
        },
        {
          text: this.$t("auditor.auditPeriods.minimal"),
          value: "minimal",
        },
        {
          text: this.$t("auditor.auditPeriods.stepModel"),
          value: "stepModel",
        },
        {
          text: this.$t("auditor.auditPeriods.new"),
          value: "new",
        },
        {
          text: this.$t("auditor.auditPeriods.editMinimal"),
          value: "editMinimal",
        },
        {
          text: this.$t("auditor.auditPeriods.editStep"),
          value: "editStep",
        },
        {
          text: this.$t("auditor.auditPeriods.secondEditStep"),
          value: "secondEditStep",
        },
        {
          text: this.$t("auditor.auditPeriods.finalEditStep"),
          value: "finalEditStep",
        },
        {
          text: this.$t("auditor.auditPeriods.auditMinimal"),
          value: "auditMinimal",
        },
        {
          text: this.$t("auditor.auditPeriods.auditStep"),
          value: "auditStep",
        },
        {
          text: this.$t("auditor.auditPeriods.secondAudit"),
          value: "secondAudit",
        },
        {
          text: this.$t("auditor.auditPeriods.interview"),
          value: "interview",
        },
        {
          text: this.$t("auditor.auditPeriods.interviewAudit"),
          value: "interviewAudit",
        },
        {
          text: this.$t("auditor.auditPeriods.finalAuditMinimal"),
          value: "finalAuditMinimal",
        },
        {
          text: this.$t("auditor.auditPeriods.finalAuditStep"),
          value: "finalAuditStep",
        },
        {
          text: this.$t("auditor.auditPeriods.closeable"),
          value: "closeable",
        },
        {
          text: this.$t("auditor.auditPeriods.done"),
          value: "done",
        },
      ];
    },

    items() {
      let index = 1;
      return map(({ id, funds, user, auditor }) => {
        return {
          id,
          number: index++,
          auditor: auditor ? `${auditor.firstname} ${auditor.lastname}` : null,
          user: user,
          userName: user.firstname + " " + user.lastname,
          name: user.company.address.name,
          lastOnlineAt: user.lastOnlineAt || "",
          contactPerson: user.useBillingAddress
            ? user.company.billingAddress.contactPerson
            : user.company.address.contactPerson,
          total: length(funds),
          minimal: countFunds("stepModel", [false], funds),
          stepModel: countFunds("stepModel", [true], funds),
          new: countFunds("state", ["NEW"], funds),
          editMinimal: countFunds("minimalRequirementsState", ["INITIAL_EDIT", "EDIT"], funds),
          auditMinimal: countFunds("minimalRequirementsState", ["AUDIT"], funds),
          finalAuditMinimal: countFunds("minimalRequirementsState", ["FINAL_AUDIT"], funds),
          editStep: countEditStep(funds),
          secondEditStep: countFunds("stepModelRequirementsState", ["SECOND_EDIT"], funds),
          auditStep: countFunds("stepModelRequirementsState", ["AUDIT"], funds),
          finalEditStep: countFunds("stepModelRequirementsState", ["FINAL_EDIT"], funds),
          finalAuditStep: countFunds("stepModelRequirementsState", ["FINAL_AUDIT"], funds),
          secondAudit: countFunds("stepModelRequirementsState", ["SECOND_AUDIT"], funds),
          interview: countFunds("stepModelRequirementsState", ["INTERVIEW"], funds),
          interviewAudit: countFunds("stepModelRequirementsState", ["INTERVIEW_AUDIT"], funds),
          closeable: countFunds("mayBeClosed", [true], funds),
          done: countFunds("state", ["DONE"], funds),
        };
      }, this.auditPeriods);
    },

    totals() {
      return {
        total: sum(pluck("total", this.items)),
        minimal: sum(pluck("minimal", this.items)),
        stepModel: sum(pluck("stepModel", this.items)),
        new: sum(pluck("new", this.items)),
        editMinimal: sum(pluck("editMinimal", this.items)),
        auditMinimal: sum(pluck("auditMinimal", this.items)),
        finalAuditMinimal: sum(pluck("finalAuditMinimal", this.items)),
        editStep: sum(pluck("editStep", this.items)),
        secondEditStep: sum(pluck("secondEditStep", this.items)),
        auditStep: sum(pluck("auditStep", this.items)),
        finalEditStep: sum(pluck("finalEditStep", this.items)),
        finalAuditStep: sum(pluck("finalAuditStep", this.items)),
        secondAudit: sum(pluck("secondAudit", this.items)),
        interview: sum(pluck("interview", this.items)),
        interviewAudit: sum(pluck("interviewAudit", this.items)),
        closeable: sum(pluck("closeable", this.items)),
        done: sum(pluck("done", this.items)),
      };
    },
  },
};
</script>
